import type { Address } from "viem";
import { z } from "zod";

/**
 * Represents a token amount with its raw value and metadata.
 * Designed to handle large integer values (wei) safely using BigInt.
 *
 * This is the standard low-level data type for asset balances in the application.
 */
export interface TokenAmount {
  /**
   * The raw amount in the smallest unit (e.g. wei).
   * Using bigint to avoid precision loss with large numbers.
   */
  amount: bigint;

  /**
   * The number of decimals for the token (e.g. 18 for ETH, 6 for USDC).
   */
  decimals: number;

  /**
   * The token symbol (e.g. "ETH", "USDC").
   */
  symbol: string;

  /**
   * The token address (optional, as it might be a native token or abstract).
   */
  address?: Address;
}

/**
 * Zod schema for TokenAmount.
 * Useful for validating TRPC inputs/outputs.
 * Note: 'amount' expects a BigInt, which works with superjson serialization.
 */
export const TokenAmountSchema = z.object({
  amount: z.bigint(),
  decimals: z.number().int().min(0).max(255),
  symbol: z.string(),
  address: z
    .custom<Address>((val) => typeof val === "string" && val.startsWith("0x"))
    .optional(),
});
